/**
 * Screen scaffolding and stack/row layout.
 *
 * These are the product's own layout components, not shadcn ones: they encode
 * decisions the design system made rather than utility classes a caller would
 * otherwise repeat — a screen whose footer stays pinned above the scroll view so
 * the cart total is always reachable, a content column that stops widening on a
 * desktop, a card grid whose column count tracks the viewport.
 *
 * Spacing is expressed through the design system's named scale rather than raw
 * Tailwind steps, because the same scale is what the two apps already use. Every
 * step maps to a literal class string: Tailwind reads class names from source
 * text, so an interpolated `gap-${size}` would never be generated.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ViewProps,
  type ViewStyle,
} from 'react-native';

import { Separator } from './ui/separator';
import { Text } from './ui/text';
import { cn } from '../lib/utils';
import { useResponsive } from '../lib/hooks';
import { useKeyboardInset, useRevealFocusedField } from '../lib/keyboard';
import { SPACING, type SpaceKey } from '../lib/theme';

/** Vertical rhythm between siblings, keyed by the design system's scale. */
const GAP: Record<SpaceKey, string> = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
  xl: 'gap-6',
  xxl: 'gap-8',
  xxxl: 'gap-12',
};

/** Horizontal inset, keyed by the design system's scale. */
const PADDING: Record<SpaceKey, string> = {
  xs: 'p-1',
  sm: 'p-2',
  md: 'p-3',
  lg: 'p-4',
  xl: 'p-6',
  xxl: 'p-8',
  xxxl: 'p-12',
};

/** Horizontal room the screen keeps for its own padding, at its widest. */

const ALIGN: Record<'start' | 'center' | 'end' | 'stretch' | 'baseline', string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const JUSTIFY: Record<'start' | 'center' | 'end' | 'between' | 'around', string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

/** Shared props for the two flex-direction helpers. */
export interface FlexProps extends Omit<ViewProps, 'style'> {
  children?: ReactNode;
  /** Gap between siblings, on the design system's scale. */
  gap?: SpaceKey;
  /** Cross-axis alignment. */
  align?: keyof typeof ALIGN;
  /** Main-axis distribution. */
  justify?: keyof typeof JUSTIFY;
  /** Wraps children onto new lines. */
  wrap?: boolean;
  /** Extra classes, merged last so they win. */
  className?: string;
}

/**
 * A vertical stack.
 *
 * @param props Gap, alignment, and children.
 * @returns The laid-out stack.
 */
export function Stack({
  children,
  gap = 'md',
  align = 'stretch',
  justify = 'start',
  wrap = false,
  className,
  ...props
}: FlexProps) {
  return (
    <View
      className={cn('flex-col', GAP[gap], ALIGN[align], JUSTIFY[justify], wrap && 'flex-wrap', className)}
      {...props}>
      {children}
    </View>
  );
}

/** A horizontal stack, with an optional `spread` shortcut for pushing the last child away. */
export interface RowProps extends FlexProps {
  /** Pushes the first and last children to opposite ends. */
  spread?: boolean;
}

/**
 * A horizontal row.
 *
 * @param props Gap, alignment, and children.
 * @returns The laid-out row.
 */
export function Row({
  children,
  gap = 'md',
  align = 'center',
  justify,
  wrap = false,
  spread = false,
  className,
  ...props
}: RowProps) {
  return (
    <View
      className={cn(
        'flex-row',
        GAP[gap],
        ALIGN[align],
        JUSTIFY[justify ?? (spread ? 'between' : 'start')],
        wrap && 'flex-wrap',
        className
      )}
      {...props}>
      {children}
    </View>
  );
}

/**
 * A wrapping row, for chips and secondary metadata that must not overflow.
 *
 * @param props Gap and children.
 * @returns The wrapping row.
 */
export function Wrap({ children, gap = 'sm', className, ...props }: FlexProps) {
  return (
    <View className={cn('flex-row flex-wrap', GAP[gap], ALIGN.center, className)} {...props}>
      {children}
    </View>
  );
}

/**
 * Flexible empty space, for pushing siblings apart inside a row.
 *
 * @returns A growing spacer view.
 */
export function Spacer() {
  return <View className="flex-1" />;
}

/**
 * A hairline rule.
 *
 * Delegates to the Reusables `Separator` rather than drawing a bare view, so the
 * rule carries the primitive's accessibility semantics and any restyling of
 * separators lands in one place.
 *
 * @param props Whether the rule is vertical, and whether it is inset from the edges.
 * @returns The separator.
 */
export function Divider({ vertical = false, inset = false }: { vertical?: boolean; inset?: boolean }) {
  return (
    <Separator
      orientation={vertical ? 'vertical' : 'horizontal'}
      className={cn(inset && (vertical ? 'my-2' : 'mx-4'))}
    />
  );
}

/**
 * How far the content must travel before an auto-hiding header reacts, in points.
 *
 * A scroll is never perfectly still — a finger resting on the glass still moves
 * the content by a pixel or two — and a header that answers every one of those
 * flickers. This is the smallest movement that counts as a direction.
 */
const HEADER_SCROLL_STEP = 6;

/** Below this offset the header is always shown, whichever way the last pixels went. */
const HEADER_REVEAL_AT = 12;

/** How long the header takes to leave or return, in milliseconds. */
const HEADER_SLIDE_MS = 180;

export interface ScreenProps {
  children: React.ReactNode;
  /** Pinned above the scrolling content; typically a header or summary bar. */
  header?: React.ReactNode;
  /** Pinned below the scrolling content; typically the cart bar or action row. */
  footer?: React.ReactNode;
  /** Constrains and centres content. Off for full-bleed dashboards. */
  constrain?: boolean;
  /**
   * Narrows the centred column, for a screen whose content should not follow the
   * viewport out to its usual tablet width. A two-field form stretched across a
   * landscape iPad is a wall of inputs; a dashboard wants the width.
   */
  columnClassName?: string;
  /** Extra classes for the scrolling content. */
  contentClassName?: string;
  className?: string;
  /**
   * Slides the header out of the way while the content is scrolled down, and
   * brings it back as soon as the content is scrolled up.
   *
   * For a header that is mostly empty, so that it costs the screen nothing once
   * the customer has started reading — the customer app's bar holds a single
   * control beside a lot of nothing. The header is laid over the content rather
   * than placed above it, which is what lets the content use the space it
   * vacates; the content is given a matching top inset so it does not start
   * underneath.
   */
  autoHideHeader?: boolean;
  /**
   * Extra clearance between an auto-hiding header and the content below it, in
   * points.
   *
   * Out of the flow, the header only reserves its own height, so the content
   * starts flush against the bar. That is right for a screen whose content opens
   * with a cover image, which would otherwise have a band of background above
   * it, and wrong for one that opens with text, which reads as though it is
   * still part of the bar. Only consulted when the header is auto-hiding.
   */
  headerGap?: number;
}

/**
 * A full-height page with optional pinned header and footer.
 *
 * The footer is rendered outside the scroll view so a cart total and its
 * checkout button stay reachable without scrolling — the single most important
 * ergonomic detail in the customer app.
 *
 * The screen also shrinks to the visual viewport while the on-screen keyboard is
 * open. Without that the scroll view keeps its full height, the browser believes
 * the focused field is already on screen, and the field and the submit button
 * sit behind the keyboard with no way to reach them.
 *
 * @param props Header, footer, content, and layout options.
 * @returns A screen container.
 */
export function Screen({
  children,
  header,
  footer,
  constrain = true,
  columnClassName,
  contentClassName,
  className,
  autoHideHeader = false,
  headerGap = 0,
}: ScreenProps) {
  const { isTablet } = useResponsive();
  const keyboard = useKeyboardInset();

  useRevealFocusedField(keyboard.open, keyboard.height);

  // ---------------------------------------------------------------------------
  // Auto-hiding header
  // ---------------------------------------------------------------------------

  // Measured rather than assumed: the header is the caller's, and only it knows
  // how tall it is.
  const [headerHeight, setHeaderHeight] = useState(0);
  const slide = useRef(new Animated.Value(0)).current;
  const lastOffset = useRef(0);
  const hidden = useRef(false);

  const setHidden = useCallback(
    (next: boolean) => {
      if (hidden.current === next) return;
      hidden.current = next;
      Animated.timing(slide, {
        toValue: next ? -headerHeight : 0,
        duration: HEADER_SLIDE_MS,
        // The native driver is asked for only where there is one. On web it is
        // missing, and asking for it anyway downgrades to the same JS animation
        // while logging a warning on every screen that scrolls.
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    },
    [slide, headerHeight]
  );

  const onScroll = useMemo(() => {
    if (!autoHideHeader) return undefined;
    return (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offset = event.nativeEvent.contentOffset.y;
      const travelled = offset - lastOffset.current;
      lastOffset.current = offset;

      // At the top there is nothing to have scrolled past, so the header
      // belongs there whatever the last few pixels did.
      if (offset <= HEADER_REVEAL_AT) {
        setHidden(false);
        return;
      }
      if (travelled > HEADER_SCROLL_STEP) setHidden(true);
      else if (travelled < -HEADER_SCROLL_STEP) setHidden(false);
    };
  }, [autoHideHeader, setHidden]);

  return (
    <View
      className={cn('bg-background flex-1', className)}
      // A measured viewport height, which no class can express. Only set while
      // the keyboard is open, so the stylesheet's `100dvh` keeps handling
      // browser chrome the rest of the time.
      //
      // `maxHeight` is what actually does the work. Inside a shell that makes
      // this a `flex: 1` item — the merchant rail's content column — the flex
      // basis is `0`, and a flex item's main size comes from its basis and its
      // growth, not from `height`. The height was set and silently ignored, so
      // every screen inside the shell kept its full height and the keyboard
      // covered the bottom of it. A maximum applies after the flex size is
      // resolved, so it clamps either way.
      style={
        keyboard.open
          ? { height: keyboard.visibleHeight, maxHeight: keyboard.visibleHeight }
          : undefined
      }>
      {/* Left exactly where it was for a header that does not move, wrapper and
          all, so a screen that has not asked for this is untouched. */}
      {autoHideHeader ? null : header}
      {autoHideHeader && header ? (
        <Animated.View
          onLayout={(event) => setHeaderHeight(event.nativeEvent.layout.height)}
          // Laid over the content rather than stacked above it: a header that
          // keeps its place in the column cannot give the space back, so the
          // content would scroll into a gap the height of the header instead of
          // into the header's space. Out of the flow it can leave, and the
          // content below it takes the room.
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            transform: [{ translateY: slide }],
          }}>
          {header}
        </Animated.View>
      ) : null}
      <ScrollView
        className="flex-1"
        onScroll={onScroll}
        scrollEventThrottle={16}
        // Two paddings for two problems. The keyboard covers the bottom of the
        // screen, so content has to scroll clear of it rather than stopping
        // underneath; and an auto-hiding header is out of the layout, so the
        // content starts below where the header sits rather than under it.
        contentContainerStyle={{
          ...(autoHideHeader && headerHeight > 0
            ? { paddingTop: headerHeight + headerGap }
            : null),
          ...(keyboard.open ? { paddingBottom: keyboard.height } : null),
        }}
        contentContainerClassName={cn(
          'flex-col gap-4 pt-4',
          isTablet ? 'px-6' : 'px-4',
          // Bottom padding keeps the last card clear of the pinned footer.
          footer ? 'pb-8' : 'pb-6',
          contentClassName
        )}>
        {constrain ? (
          <View
            className={cn(
              // The gap has to be here as well as on the container above: a
              // constrained screen wraps its children in this view, so the
              // container's `gap-4` would otherwise sit between the container
              // and its only child and do nothing. It went unnoticed because
              // almost every screen passes one child — the item editor is the
              // one that passes a title and a form, and they met with no space.
              'w-full flex-col gap-4 self-center',
              isTablet ? 'max-w-6xl' : 'max-w-xl',
              columnClassName
            )}>
            {children}
          </View>
        ) : (
          children
        )}
      </ScrollView>
      {footer}
    </View>
  );
}

/**
 * A wrapping grid of cards.
 *
 * The column count comes from the viewport rather than a media query, so the
 * three merchant screens that show card grids — orders, menu, tables — agree
 * without each carrying its own magic numbers.
 *
 * @param props Children and an optional minimum card width in points.
 * @returns A wrapping row of equal-width cells.
 */
const CARD_GAP = 16;

export function CardGrid({
  children,
  minWidth = 260,
  maxWidth,
  columns: fixedColumns,
  spans,
  className,
}: {
  children: ReactNode;
  /** Narrowest a cell may become before the grid drops to fewer columns. */
  minWidth?: number;
  /**
   * How many columns each child takes, in order. Defaults to one each.
   *
   * A parallel array rather than a prop on the child, because the grid owns the
   * columns and the child does not know what it is being laid into. It only makes
   * sense with a pinned `columns`: with a count worked out from the viewport, the
   * number of columns changes and the spans stop adding up.
   */
  spans?: number[];
  /**
   * Pin the column count, letting the cells narrow to fit rather than dropping
   * to fewer columns. For a board where a count that changes with the window is
   * more distracting than narrow cards.
   */
  columns?: number;
  /** Widest a cell may grow, so a short last row does not stretch to fill it. */
  maxWidth?: number;
  className?: string;
}) {
  const { width } = useResponsive();

  // A percentage basis keeps the cells aligned; the minimum width decides how
  // many fit before wrapping. A pinned count ignores the minimum, so its cells
  // narrow rather than dropping to fewer.
  const columns = fixedColumns ?? Math.max(1, Math.floor(width / minWidth));
  const basis = 100 / columns;

  // The gutter is padding inside each cell rather than a gap between them. A gap
  // is counted when the line is broken, so two cells asking 50% each plus 16px
  // overflow and the second takes its own row — the gap deciding the count.
  // Padding lives inside the 50% and cannot.
  // No growing: a cell is exactly its share, so a short last row keeps the same
  // card width instead of stretching one to fill the row. `maxWidth` is still
  // honoured for a caller that wants to cap a single-column cell.
  const cell: ViewStyle = {
    flexGrow: 0,
    flexShrink: 1,
    // Explicit, because the CSS default is `min-width: auto`: a card whose
    // content cannot wrap then refuses to shrink below it, outgrows its share
    // and paints over the next one — a clipped badge, on the first card, on the
    // browsers that apply the default.
    minWidth: 0,
    flexBasis: `${basis}%`,
    paddingRight: CARD_GAP,
    paddingBottom: CARD_GAP,
    ...(maxWidth === undefined ? {} : { maxWidth }),
  };

  return (
    <View className={cn('w-full flex-row flex-wrap', className)}>
      {Array.isArray(children) ? (
        children.map((child, index) => (
          <View
            key={index}
            style={
              spans?.[index] && spans[index] !== 1
                ? { ...cell, flexBasis: `${basis * (spans[index] ?? 1)}%` }
                : cell
            }>
            {child}
          </View>
        ))
      ) : (
        <View style={cell}>{children}</View>
      )}
    </View>
  );
}

/**
 * A page title with an optional subtitle and trailing action.
 *
 * Every in-content page heading is this, so the app has one title style and one
 * place to change it. Two things about it are deliberate:
 *
 * The title is **bolder than a section's**. A `CardTitle` is `font-semibold`,
 * and `Section` below sets its own title the same way; a page heading at that
 * weight sits level with the cards underneath it and stops reading as the top of
 * the screen. It is also the app's only `aria-level={1}` — a card is 3 — so a
 * screen reader gets an outline rather than a flat run of headings.
 *
 * `wrap` and `minWidth` are how an action beside the title behaves: it drops to
 * its own line rather than squeezing the title, and the floor is what the row
 * breaks on — the break comes when floor + gap + action exceeds the width,
 * before anything visibly collides. The floor is a prop because it is only
 * meaningful against the action beside it: one button can afford 240, a row of
 * five controls cannot. It is a `style` rather than a class because the number
 * varies per screen and Tailwind cannot generate a class at runtime.
 *
 * The subtitle takes a node as well as a string: most screens have a sentence
 * there, which is set as the muted subtitle, but the orders board and the cook
 * page have a row of counts whose colours carry meaning, and those are rendered
 * as given — each count through `HeadingStat`, so both paths set the same type.
 * A node builds its own line, and react-native-web passes a colour down to a
 * nested `Text` but not a size: a count that named its own size read 12px
 * against every sentence's 14px until it went through that component.
 *
 * @param props Title, subtitle, action, and the title block's floor.
 * @returns The heading block.
 */
export function PageHeading({
  title,
  subtitle,
  action,
  minWidth = 240,
  wrap = true,
  className,
}: {
  title: string;
  /** A sentence, set muted; or a node, for a row of counts. */
  subtitle?: ReactNode;
  action?: ReactNode;
  /** Floor for the title block, in points. Set it against the action's width. */
  minWidth?: number;
  /** Whether the action takes its own line rather than squeezing the title. */
  wrap?: boolean;
  className?: string;
}) {
  return (
    <View
      className={cn('flex-row items-center justify-between gap-4', wrap && 'flex-wrap', className)}>
      <View className="flex-1 flex-col gap-1" style={{ minWidth }}>
        <Text variant="h3" aria-level={1} className="text-left font-bold">
          {title}
        </Text>
        {typeof subtitle === 'string' ? (
          <Text variant="muted">{subtitle}</Text>
        ) : (
          (subtitle ?? null)
        )}
      </View>
      {action}
    </View>
  );
}

/** What a count on a heading's subtitle line is saying, by colour. */
const HEADING_STAT_TONE = {
  muted: '',
  destructive: 'text-destructive font-bold',
  success: 'text-success font-bold',
  warning: 'text-warning font-bold',
};

/**
 * One item on a page heading's subtitle line.
 *
 * For the screens that compose that line rather than passing a sentence: a row
 * of counts, a late count in red, a status chip. It is the same muted subtitle
 * `PageHeading` sets for a string, so a line built by hand cannot come out at a
 * different size from one written as a sentence.
 *
 * @param props The count, and the colour it is saying something in.
 * @returns The subtitle's text.
 */
export function HeadingStat({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: keyof typeof HEADING_STAT_TONE;
}) {
  return (
    <Text variant="muted" className={HEADING_STAT_TONE[tone]}>
      {children}
    </Text>
  );
}

/**
 * A titled block of content, used to break up long forms and dashboards.
 *
 * @param props Title, optional subtitle, optional trailing action, and children.
 * @returns The section.
 */
export function Section({
  title,
  subtitle,
  action,
  children,
  gap = 'md',
  className,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  gap?: SpaceKey;
  className?: string;
}) {
  return (
    <View className={cn('flex-col', GAP[gap], className)}>
      <Row spread gap="md" align="start">
        <View className="flex-1 flex-col gap-1">
          <Text className="text-lg font-semibold">{title}</Text>
          {subtitle ? <Text variant="muted">{subtitle}</Text> : null}
        </View>
        {action}
      </Row>
      {children}
    </View>
  );
}

/**
 * A bottom-anchored action bar.
 *
 * Uses a top border rather than a shadow so it reads as attached to the scroll
 * content above it, and honours the home-indicator inset on iOS.
 *
 * @param props Children and extra classes.
 * @returns The pinned bar.
 */
export function BottomBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <View className={cn('bg-background border-border border-t px-4 py-3 pb-6', className)}>
      {children}
    </View>
  );
}

/**
 * A pressable row with a hit area, for list rows and cards that happen to be
 * tappable rather than for buttons.
 *
 * The pressed state is the only affordance on a card that is entirely tappable;
 * without it the surface looks inert. `active:` is how NativeWind expresses that
 * on a `Pressable`, which is the same mechanism the Reusables components use.
 *
 * @param props `onPress`, children, and styling.
 * @returns The pressable surface.
 */
export function Touchable({
  children,
  onPress,
  disabled,
  className,
  accessibilityLabel,
  testID,
}: {
  children: ReactNode;
  onPress?: () => void;
  disabled?: boolean;
  className?: string;
  accessibilityLabel?: string;
  testID?: string;
}) {
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      disabled={disabled || !onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      className={cn('active:opacity-70', disabled && 'opacity-50', className)}>
      {children}
    </Pressable>
  );
}

export { GAP as GAP_CLASSES, PADDING as PADDING_CLASSES, SPACING };
